import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography, Alert, Spin } from "antd";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import {
  isActiveBillingPeriod,
  selectCanonicalActiveBillingPeriod,
} from "../../../utils/billingPeriods";
import DEFUNTO_ANNUAL_STATS_QUERY from "../queries/defuntoAnnualStats";

const { Text } = Typography;

// Renders past billing periods and emits current active billing via onActive callback
const OrgDefuntiAnnual = ({ orgId, dateRef = "created_at", onActive }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError("");
      try {
        const token = await getTokenSilently();
        const body = {
          query: DEFUNTO_ANNUAL_STATS_QUERY,
          variables: { orgId, dateRef },
        };
        const resp = await fetch(graphqlUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        const json = await resp.json();
        if (json.errors) throw new Error(json.errors[0]?.message || "GraphQL error");
        const edges = json?.defunto_period_stats?.edges || [];
        const list = edges.map((e) => e.node);
        if (!cancelled) setRows(list);
      } catch (e) {
        console.error("[OrgDefuntiAnnual] fetch error", e);
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (orgId) run();
    return () => { cancelled = true; };
  }, [orgId, dateRef, getTokenSilently, graphqlUrl]);

  // No extra active query: determine active from the loaded rows

  const columns = useMemo(() => [
    {
      title: "Periodo",
      key: "period",
      render: (_, r) => {
        const start = r?.period_start ? new Date(r.period_start) : null;
        const end = r?.period_end ? new Date(r.period_end) : null;
        const fmt = (d) => (d ? d.toLocaleDateString("it-IT") : "-");
        return <Text>{fmt(start)} → {fmt(end)}</Text>;
      },
      sorter: (a, b) => new Date(a.period_start) - new Date(b.period_start),
    },
    {
      title: "Totale Defunti",
      dataIndex: "total_count",
      key: "total_count",
      width: 160,
      sorter: (a, b) => (a.total_count || 0) - (b.total_count || 0),
    },
  ], []);

  // Separate active billing from others using the loaded list only
  const nowMs = Date.now();
  const active = useMemo(
    () => selectCanonicalActiveBillingPeriod(rows, nowMs),
    [rows, nowMs]
  );

  // Overlapping active rows belong to obsolete subscription anchors.
  const past = useMemo(
    () => rows.filter((row) => !isActiveBillingPeriod(row, nowMs)),
    [rows, nowMs]
  );
  const pastSorted = useMemo(
    () => [...past].sort((a, b) => new Date(b.period_start) - new Date(a.period_start)),
    [past]
  );

  // Notify parent about active period
  useEffect(() => {
    if (onActive) onActive(active || null);
  }, [onActive, active]);

  if (loading) return <Spin />;
  if (error) return <Alert type="error" message="Errore nel caricamento" description={error} showIcon />;

  return (
    <Table
      size="small"
      rowKey={(r) => r.period_key || `${r.period_start}-${r.period_end}`}
      dataSource={pastSorted}
      columns={columns}
      pagination={false}
    />
  );
};

export default OrgDefuntiAnnual;
